import { Map } from "immutable"
import {
    FilterExpression,
    UnaryFilterOperator,
    UnaryFilterExpression
} from "../../../../../../../../../../../Models/Client/FilterExpression"
import {
    TaskLabelerConfig,
    LabelerConfig
} from "../../../../../../../../../../../Models/DataProgram/LabelerRestriction"
import { APIAppPlan } from "../../../../../../../../../../../Models/API/APIAppPlans"
import { APIAppInstanceModel } from "../../../../../../../../../../../Models/API/AppInstanceModel"
import { isEmail } from "../../../../../../../../../../../Helpers/Functions/string"
import {
    APIAppMeasurerConfiguration,
    getDefaultMeasurementValue
} from "../../../../../../../../../../../Models/API/APIAppMeasurerConfiguration"
import {
    APIAppInstanceMetricModel,
    MetricFields
} from "../../../../../../../../../../../Models/API/AppMetricModel"
import { APIAppTemplateModel } from "../../../../../../../../../../../Models/API/AppTemplateModel"

export interface FormFields {
    plan: FormFieldsPlan
    whoShouldLabel: FormFieldsWhoShouldLabel
    labelerRestrictions: FormFieldsLabelerRestriction[]
    labelerPayments: Map<string, number | undefined>
    measurement?: APIAppMeasurerConfiguration
    measurerMetrics?: MetricFields[] // for convinience of adding/removing metrics, this is an array of key-value object, final output would aggregate all key-value object to a single key-value object
    cancelAndRelabelJobsInState: string[]
}

export interface WorkflowConfig {
    priceOverride?: number
    hiddenFromCustomer: boolean
}

export interface FormFieldsPlan {
    workflowConfigs: Map<string, WorkflowConfig>
    selectedWorkflow: string | undefined
}

export interface FormFieldsWhoShouldLabel {
    isCanoticCrowd: boolean
    isVisible: boolean
}

export const ALL_TASKS = Symbol("*")

export type TaskLabelerRestriction = Omit<
    TaskLabelerConfig,
    "priceTag" | "amount" | "timeToExpire" | "timeToResolve" | "timeToUpdate"
>

export type RestrictionFilter = FilterExpression<TaskLabelerRestriction>

export type TaskName = string | typeof ALL_TASKS

export interface FormFieldsLabelerRestriction {
    /**
     * Task name to apply restriction
     */
    taskName: TaskName
    restriction: RestrictionFilter[]
}

export const defaultRestriction: RestrictionFilter = {
    fieldName: "emails",
    operator: UnaryFilterOperator.eq,
    value: []
}

export const defaultWorkflowConfig: WorkflowConfig = {
    hiddenFromCustomer: false,
    priceOverride: undefined
}

export function getVisibleWorkflows(value: FormFields): string[] {
    return [
        ...value.plan.workflowConfigs
            .filter(config => !config.hiddenFromCustomer)
            .keys()
    ]
}

export function setAllPlansVisibility(
    value: FormFieldsPlan,
    visible: boolean
): FormFieldsPlan {
    return {
        ...value,
        workflowConfigs: value.workflowConfigs.map(config => ({
            ...config,
            hiddenFromCustomer: !visible
        }))
    }
}

export function togglePlanVisibility(
    value: FormFieldsPlan,
    workflow: string
): FormFieldsPlan {
    const workflowConfig = value.workflowConfigs.get(workflow)
    return {
        ...value,
        workflowConfigs:
            workflowConfig === undefined
                ? value.workflowConfigs
                : value.workflowConfigs.set(workflow, {
                      ...workflowConfig,
                      hiddenFromCustomer: !workflowConfig.hiddenFromCustomer
                  })
    }
}

function restrictionToFilters({
    id,
    emails = [],
    groups = [],
    metrics = [],
    excluded = [],
    excludedGroups = [],
    qualifierTestId
}: TaskLabelerRestriction): RestrictionFilter[] {
    const filters: RestrictionFilter[] = []

    if (id !== undefined) {
        filters.push({
            fieldName: "id",
            operator: UnaryFilterOperator.eq,
            value: id
        })
    }

    if (emails.length > 0) {
        filters.push({
            fieldName: "emails",
            operator: UnaryFilterOperator.eq,
            value: emails
        })
    }

    if (groups.length > 0) {
        filters.push({
            fieldName: "groups",
            operator: UnaryFilterOperator.eq,
            value: groups
        })
    }

    if (metrics.length > 0) {
        filters.push(
            ...metrics.map(
                m =>
                    ({
                        fieldName: "metrics",
                        operator: UnaryFilterOperator.eq,
                        value: [m]
                    } as RestrictionFilter)
            )
        )
    }

    if (excluded.length > 0) {
        filters.push({
            fieldName: "excluded",
            operator: UnaryFilterOperator.eq,
            value: excluded
        })
    }

    if (excludedGroups.length > 0) {
        filters.push({
            fieldName: "excludedGroups",
            operator: UnaryFilterOperator.eq,
            value: excludedGroups
        })
    }

    if (qualifierTestId !== undefined) {
        filters.push({
            fieldName: "qualifierTestId",
            operator: UnaryFilterOperator.eq,
            value: qualifierTestId
        })
    }
    return filters
}

function filtersToRestriction(
    filters: RestrictionFilter[]
): TaskLabelerRestriction {
    return filters.reduce<TaskLabelerRestriction>((passed, filter) => {
        switch (filter.operator) {
            case UnaryFilterOperator.eq:
                switch (filter.fieldName) {
                    case "id":
                        return { ...passed, id: filter.value }
                    case "metrics":
                        return {
                            ...passed,
                            metrics: (passed.metrics ?? []).concat(
                                filter.value !== undefined &&
                                    filter.value.every(
                                        f => !Number.isNaN(f.value)
                                    )
                                    ? filter.value
                                    : []
                            )
                        }
                    case "emails":
                        return {
                            ...passed,
                            emails: filter.value?.filter(s => s !== "")
                        }
                    case "excluded":
                        return {
                            ...passed,
                            excluded: filter.value?.filter(
                                s => !Number.isNaN(s)
                            )
                        }
                    case "groups":
                        return {
                            ...passed,
                            groups: filter.value?.filter(s => s !== "")
                        }
                    case "excludedGroups":
                        return {
                            ...passed,
                            excludedGroups: filter.value?.filter(s => s !== "")
                        }
                    case "qualifierTestId":
                        return {
                            ...passed,
                            qualifierTestId: filter.value
                        }
                    default:
                        return passed
                }
            default:
                return passed
        }
    }, {})
}

export function formValuesToRestriction(
    taskNames: string[],
    value: FormFields,
    initialValue: LabelerConfig,
    defaultValue: LabelerConfig
): LabelerConfig {
    return taskNames.reduce<LabelerConfig>(
        (passed, taskName) => {
            const initial = initialValue.taskConstraints?.[taskName]
            const _default = defaultValue.taskConstraints?.[taskName]
            const restriction = value.labelerRestrictions.find(
                r => r.taskName === taskName
            )?.restriction
            const payment = value.labelerPayments.get(taskName)
            const entry = {
                ...filtersToRestriction(restriction ?? []),
                priceTag: initial?.priceTag ?? _default?.priceTag,
                amount: payment ?? initial?.amount,
                timeToExpire: initial?.timeToExpire,
                timeToResolve: initial?.timeToResolve,
                timeToUpdate: initial?.timeToUpdate
            }

            return Object.values(entry).filter(item => item !== undefined)
                .length === 0
                ? passed
                : {
                      ...passed,
                      taskConstraints: {
                          ...passed.taskConstraints,
                          [taskName]: entry
                      }
                  }
        },
        {
            priceTag: initialValue.priceTag ?? defaultValue.priceTag,
            amount: initialValue.amount,
            timeToExpire: initialValue.timeToExpire,
            timeToResolve: initialValue.timeToResolve,
            timeToUpdate: initialValue.timeToUpdate,
            ...filtersToRestriction(
                value.labelerRestrictions.find(
                    ({ taskName }) => taskName === ALL_TASKS
                )?.restriction ?? []
            )
        }
    )
}

export function restrictionToFormValues(
    taskNames: string[],
    { taskConstraints, ...allTasksConstraints }: LabelerConfig
): FormFieldsLabelerRestriction[] {
    const perTaskFilters = taskNames.flatMap(taskName => {
        const taskFilters = restrictionToFilters(
            taskConstraints?.[taskName] ?? {}
        )

        return taskFilters.length === 0
            ? []
            : [{ taskName, restriction: taskFilters }]
    })
    const allTasksFilters = restrictionToFilters(allTasksConstraints)

    if (
        allTasksFilters.length === 0 &&
        taskNames.length === 1 &&
        perTaskFilters.length === 1
    ) {
        return [
            { taskName: ALL_TASKS, restriction: perTaskFilters[0].restriction }
        ]
    }

    return ([] as FormFieldsLabelerRestriction[]).concat(
        (allTasksFilters.length === 0
            ? []
            : [
                  {
                      taskName: ALL_TASKS,
                      restriction: allTasksFilters
                  } as FormFieldsLabelerRestriction
              ]
        ).concat(perTaskFilters)
    )
}

export function createInitialValue(
    taskNames: string[],
    plansMetadata: APIAppPlan[],
    instance: APIAppInstanceModel,
    template: APIAppTemplateModel,
    measurerConfiguration?: APIAppMeasurerConfiguration,
    appMeasurerMetrics?: APIAppInstanceMetricModel[]
): FormFields {
    const constraints = instance.constraints ?? {}

    return {
        plan: {
            selectedWorkflow: instance.selectedWorkflow,
            workflowConfigs: plansMetadata.reduce<Map<string, WorkflowConfig>>(
                (passed, plan) =>
                    passed.set(plan.workflow, {
                        hiddenFromCustomer:
                            !instance.dpWorkflows?.includes(plan.workflow) ??
                            true,
                        priceOverride: undefined
                    }),
                Map<string, WorkflowConfig>()
            )
        },
        whoShouldLabel: {
            isCanoticCrowd: instance.canoticCrowd,
            isVisible: template.metadata.ai_workflow !== true
        },
        labelerRestrictions: restrictionToFormValues(taskNames, constraints),
        labelerPayments: Map<string, number | undefined>(
            Object.entries(constraints.taskConstraints ?? {}).map(
                ([taskName, constraint]) => [taskName, constraint.amount]
            )
        ),
        measurement: getDefaultMeasurementValue(measurerConfiguration),
        cancelAndRelabelJobsInState: [],
        measurerMetrics:
            appMeasurerMetrics === undefined || appMeasurerMetrics.length === 0
                ? undefined
                : appMeasurerMetrics.map(({ metric_name, fields }) => ({
                      [metric_name]: fields ?? {}
                  }))
    }
}

export function getEmailValidationErrors(
    form: FormFieldsLabelerRestriction[]
): Map<TaskName, string> {
    type EmailField = UnaryFilterExpression<
        Pick<TaskLabelerRestriction, "emails">
    >

    return form.reduce((passed, filter) => {
        const emailFilter = filter.restriction.find(
            (r): r is EmailField =>
                r.fieldName === "emails" &&
                r.operator === UnaryFilterOperator.eq
        )
        const invalidEmails =
            emailFilter?.value?.filter(
                email => email !== "" && !isEmail(email)
            ) ?? []

        return invalidEmails.length === 0
            ? passed
            : passed.set(
                  filter.taskName,
                  invalidEmails.length > 1
                      ? `${invalidEmails.join(", ")} are not email`
                      : `${invalidEmails[0]} is not an email`
              )
    }, Map<TaskName, string>())
}
